<script>
    import TruckSelector from "./TruckSelector.svelte";
    import LoadsSummaryView from "./LoadsSummaryView.svelte";
    import LoadsDetailView from "./LoadsDetailView.svelte";

    import {
        extractJobLoads,
        exportToCSV,
        formatDateLong,
        getCustomerName,
        formatDateTime,
        formatDuration
    } from "./reportHelpers.js";

    let reportMode = 'job'; // 'job' | 'date'
    let activeStatusScope = 'all'; // 'all' | 'active' | 'completed'
    let selectedJobId = 'all';
    let selectedDate = '';
    let selectedTruckId = 'all';
    let activeViewMode = 'summary'; // 'summary' | 'detail'

    let matchingJobs = [];
    let searchTerm = '';

    function handleReportEvent(detail) {
        if (!detail) return;
        reportMode = detail.reportMode || 'job';
        activeStatusScope = detail.statusScope || 'all';
        selectedJobId = detail.selectedJobId || 'all';
        selectedDate = detail.selectedDate || '';
        selectedTruckId = detail.selectedTruckId || 'all';
        activeViewMode = detail.activeViewMode || activeViewMode;
        matchingJobs = detail.jobs || [];
    }

    // Process all matching loads based on selected parameters
    $: reportData = extractJobLoads({
        jobs: matchingJobs,
        reportMode,
        selectedDate,
        selectedJobId,
        selectedTruckId
    });

    $: loads = reportData.loads;
    $: truckSummaries = reportData.truckSummaries;
    $: kpis = reportData.kpis;

    // Available trucks for quick filter bar in detail view
    $: availableTruckRegos = Array.from(new Set(loads.map(l => l.rego)));

    // Dynamic title
    $: currentJobObj = matchingJobs.find(j => String(j.id) === String(selectedJobId) || String(j.job_number) === String(selectedJobId));

    $: dynamicReportTitle = (() => {
        if (reportMode === 'date') {
            const dateStr = selectedDate ? formatDateLong(selectedDate) : 'Today';
            if (selectedJobId !== 'all' && currentJobObj) {
                return `${getCustomerName(currentJobObj)} • Daily Loads Report (${dateStr})`;
            }
            return `Daily Loads Report • ${dateStr}`;
        } else {
            if (currentJobObj) {
                const jNum = currentJobObj.job_number || `Job #${currentJobObj.id}`;
                const cust = getCustomerName(currentJobObj);
                return `${jNum} - ${cust} • Entire Job Loads Report`;
            }
            return `All Jobs • Entire Operations Loads Report`;
        }
    })();

    // Drill down from summary view directly to a specific truck's load log
    function handleTruckDrillDown(truckRego) {
        selectedTruckId = truckRego;
        activeViewMode = 'detail';
    }

    // Export CSV based on currently active view
    function handleExportCSV() {
        const timestamp = selectedDate || new Date().toISOString().slice(0, 10);

        if (activeViewMode === 'summary') {
            exportToCSV(`trucktrack-summary-${reportMode}-${timestamp}`, [
                { key: 'rego', label: 'Truck Rego' },
                { key: 'driver', label: 'Driver' },
                { key: 'capacity', label: 'Capacity (t)' },
                { key: 'jobNumber', label: 'Job #' },
                { key: 'customerName', label: 'Customer' },
                { key: 'materialName', label: 'Material' },
                { key: 'firstLoadTime', label: 'First Load' },
                { key: 'lastLoadTime', label: 'Last Tip' },
                { key: 'loadsCount', label: 'Completed Loads' },
                { key: 'totalTonnes', label: 'Total Hauled (t)' },
                { key: 'avgCycleMins', label: 'Avg Turnaround (min)' }
            ], truckSummaries);
        } else {
            exportToCSV(`trucktrack-loads-log-${reportMode}-${timestamp}`, [
                { key: 'loadIndex', label: 'Load #' },
                { key: 'rego', label: 'Truck Rego' },
                { key: 'driver', label: 'Driver' },
                { key: 'jobNumber', label: 'Job #' },
                { key: 'customerName', label: 'Customer' },
                { key: 'materialName', label: 'Material' },
                { key: 'loadTime', label: 'Load Departure Time' },
                { key: 'unloadTime', label: 'Disposal Arrival Time' },
                { key: 'tripMins', label: 'Transit Duration (min)' },
                { key: 'cycleMins', label: 'Cycle Turnaround (min)' },
                { key: 'docket', label: 'Docket / Ticket #' },
                { key: 'issues', label: 'Status' }
            ], loads);
        }
    }

    function handlePrint() {
        window.print();
    }
</script>

<div class="reports-main-layout">
    <!-- Left / Center: Main Report Area -->
    <main class="reports-content">
        <!-- Top Toolbar Header -->
        <header class="reports-toolbar">
            <div class="toolbar-left">
                <div class="title-with-pill">
                    <h2 class="report-main-title">{dynamicReportTitle}</h2>
                    <span class="scope-pill scope-{activeStatusScope}">
                        {activeStatusScope === 'all' ? 'All Jobs' : (activeStatusScope === 'active' ? 'Active' : 'Completed')}
                    </span>
                    <span class="mode-pill">
                        {reportMode === 'date' ? 'By Date' : 'Entire Job'}
                    </span>
                </div>
                <span class="report-meta-subtitle">
                    Showing {kpis.totalLoads} Loads • {kpis.activeTrucksCount} Active Vehicles • Real-time DB Sync
                </span>
            </div>

            <!-- View Switcher & Action Controls -->
            <div class="toolbar-actions no-print">
                <!-- Sub-view switcher: Summary vs Individual Truck -->
                <div class="view-mode-tabs">
                    <button
                        type="button"
                        class="tab-btn {activeViewMode === 'summary' ? 'active' : ''}"
                        on:click={() => activeViewMode = 'summary'}
                    >
                        <span>📊 Summary</span>
                    </button>
                    <button
                        type="button"
                        class="tab-btn {activeViewMode === 'detail' ? 'active' : ''}"
                        on:click={() => activeViewMode = 'detail'}
                    >
                        <span>🚚 Individual Truck ({kpis.totalLoads})</span>
                    </button>
                </div>

                <!-- Live Search Box -->
                <div class="search-box-wrap">
                    <input
                        type="text"
                        class="search-input"
                        placeholder="Search loads & dockets..."
                        bind:value={searchTerm}
                    />
                    {#if searchTerm}
                        <button type="button" class="btn-clear-search" on:click={() => searchTerm = ''}>✕</button>
                    {/if}
                </div>

                <!-- Export CSV Button -->
                <button
                    type="button"
                    class="action-btn btn-export"
                    on:click={handleExportCSV}
                    title="Export currently displayed table to CSV"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Export CSV</span>
                </button>

                <!-- Print / PDF Button -->
                <button
                    type="button"
                    class="action-btn btn-print"
                    on:click={handlePrint}
                    title="Print or Save as PDF"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    <span>Print</span>
                </button>
            </div>
        </header>

        <!-- Main Report Body View -->
        <div class="report-body">
            {#if activeViewMode === 'summary'}
                <LoadsSummaryView
                    summaries={truckSummaries}
                    {kpis}
                    {searchTerm}
                    {reportMode}
                    reportTitle={dynamicReportTitle}
                    on:selectTruck={(e) => handleTruckDrillDown(e.detail.truckId)}
                />
            {:else}
                <LoadsDetailView
                    {loads}
                    bind:selectedTruckId
                    availableTrucks={availableTruckRegos}
                    {searchTerm}
                    reportTitle={dynamicReportTitle}
                />
            {/if}
        </div>
    </main>

    <!-- Right Column: Job & Loads Filter Sidebar -->
    <aside class="reports-sidebar no-print">
        <TruckSelector
            bind:reportMode
            bind:activeStatusScope
            bind:selectedJobId
            bind:selectedDate
            bind:selectedTruckId
            bind:activeViewMode
            on:generateReport={(e) => handleReportEvent(e.detail)}
        />
    </aside>
</div>

<style>
    .reports-main-layout {
        display: grid;
        grid-template-columns: 1fr 340px; /* Content on LEFT, Job Selector on RIGHT */
        gap: 1.25rem;
        padding: 1.25rem;
        height: 100%;
        overflow: hidden;
        background-color: var(--tt-slate-100);
    }

    .reports-content {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-right: 4px;
    }

    .reports-sidebar {
        overflow-y: auto;
    }

    .reports-toolbar {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-lg);
        padding: 1rem 1.25rem;
        box-shadow: var(--tt-shadow-sm);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
    }

    .toolbar-left {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .title-with-pill {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .report-main-title {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 700;
        color: var(--tt-slate-900);
    }

    .scope-pill {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .scope-all {
        background-color: #f1f5f9;
        color: #334155;
        border: 1px solid #cbd5e1;
    }

    .scope-active {
        background-color: #dbeafe;
        color: #1e40af;
        border: 1px solid #bfdbfe;
    }

    .scope-completed {
        background-color: #dcfce7;
        color: #15803d;
        border: 1px solid #bbf7d0;
    }

    .mode-pill {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        background-color: #ede9fe;
        color: #6d28d9;
        border: 1px solid #ddd6fe;
    }

    .report-meta-subtitle {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .toolbar-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .view-mode-tabs {
        display: flex;
        background-color: var(--tt-slate-100);
        padding: 2px;
        border-radius: var(--tt-radius-sm);
        gap: 2px;
        border: 1px solid var(--tt-slate-200);
    }

    .tab-btn {
        background: transparent;
        border: none;
        padding: 5px 10px;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--tt-slate-600);
        border-radius: var(--tt-radius-sm);
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
    }

    .tab-btn.active {
        background-color: #ffffff;
        color: var(--tt-primary);
        font-weight: 700;
        box-shadow: 0 1px 2px rgba(0,0,0,0.08);
    }

    .search-box-wrap {
        position: relative;
        display: flex;
        align-items: center;
    }

    .search-input {
        background-color: var(--tt-slate-50);
        border: 1px solid var(--tt-slate-300);
        border-radius: var(--tt-radius-sm);
        padding: 6px 26px 6px 10px;
        font-size: 0.8125rem;
        color: var(--tt-slate-800);
        width: 180px;
        transition: all 0.15s ease;
    }

    .search-input:focus {
        outline: none;
        border-color: var(--tt-primary);
        background-color: #ffffff;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        width: 220px;
    }

    .btn-clear-search {
        position: absolute;
        right: 6px;
        background: transparent;
        border: none;
        color: var(--tt-slate-400);
        cursor: pointer;
        font-size: 0.75rem;
    }

    .action-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        font-size: 0.8125rem;
        font-weight: 600;
        border-radius: var(--tt-radius-sm);
        border: 1px solid var(--tt-slate-300);
        background-color: #ffffff;
        color: var(--tt-slate-700);
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
    }

    .action-btn:hover {
        background-color: var(--tt-slate-50);
        border-color: var(--tt-slate-400);
        color: var(--tt-slate-900);
    }

    .btn-export {
        color: var(--tt-primary);
        border-color: var(--tt-primary-border);
        background-color: var(--tt-primary-light);
    }

    .btn-export:hover {
        background-color: #dbeafe;
        color: var(--tt-primary-hover);
    }

    .report-body {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    /* Print styles */
    @media print {
        .no-print {
            display: none !important;
        }

        .reports-main-layout {
            display: block !important;
            padding: 0 !important;
            background: #ffffff !important;
        }

        .reports-content {
            overflow: visible !important;
        }

        .reports-toolbar {
            box-shadow: none !important;
            border-bottom: 2px solid #000000 !important;
        }
    }

    @media (max-width: 1024px) {
        .reports-main-layout {
            grid-template-columns: 1fr;
            overflow-y: auto;
        }
    }
</style>